import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useAllocateHintProperties } from '~/hooks/apis/connection-google/useAllocateHintProperties';
import { hintCoverageGridQueryKeys } from '~/hooks/apis/connection-google/useHintCoverageGrid';
import { hintCoverageQueryKeys } from '~/hooks/apis/connection-google/useHintCoverageMatrix';

import { HintCoverageAccountType } from '~/services/ConnectionGoogleService';

import AllocatedPropertiesSearch from './AllocatedPropertiesSearch';
import HintCoverageDetailedMatrix from './HintCoverageDetailedMatrix';
import HintCoverageGridSummary from './HintCoverageGridSummary';

const ACCOUNT_TYPE_OPTIONS: Array<{ value: HintCoverageAccountType; label: string }> = [
  { value: 'lpc', label: 'LPC' },
  { value: 'lpp', label: 'LPP' },
  { value: 'scoopon', label: 'Scoopon' },
  { value: 'le_vacation_rental', label: 'LE Vacation Rental' },
];

const SUMMARY_TAB = 0;
const DETAILED_TAB = 1;

export default function HintCoverageMatrixContainer() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [accountType, setAccountType] = useState<HintCoverageAccountType>('lpc');
  const [windowHours, setWindowHours] = useState(5);
  const [activeTab, setActiveTab] = useState(SUMMARY_TAB);
  const { mutate: allocateHintProperties, isPending: isAllocating } = useAllocateHintProperties();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: hintCoverageGridQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: hintCoverageQueryKeys.all });
  };

  return (
    <Container maxWidth="xl">
      <PageSubheader title="Pull With Hints Coverage">
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: theme.spacing(22.5) }}>
            <InputLabel>Account Type</InputLabel>
            <Select<HintCoverageAccountType>
              value={accountType}
              label="Account Type"
              onChange={(e) => setAccountType(e.target.value)}
            >
              {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Window (hrs)"
            type="number"
            value={windowHours}
            onChange={(e) => setWindowHours(Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
            slotProps={{ htmlInput: { min: 1, max: 24 } }}
            sx={{ width: theme.spacing(15) }}
          />

          <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Stack>
      </PageSubheader>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mt: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Summary" />
          <Tab label="Detailed Coverage" />
        </Tabs>
        <Button variant="contained" color="primary" onClick={() => setConfirmDialogOpen(true)}>
          Re-classify Properties
        </Button>
      </Box>

      {activeTab === SUMMARY_TAB && <HintCoverageGridSummary accountType={accountType} windowHours={windowHours} />}

      {activeTab === DETAILED_TAB && <HintCoverageDetailedMatrix accountType={accountType} windowHours={windowHours} />}

      <AllocatedPropertiesSearch accountType={accountType} />

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Re-classify Properties</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to re-classify properties for account type{' '}
            <strong>{accountType.toUpperCase()}</strong>? This will queue a background reclassification job.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            loading={isAllocating}
            onClick={() => {
              allocateHintProperties(accountType, {
                onSuccess: () => setConfirmDialogOpen(false),
                onError: () => setConfirmDialogOpen(false),
              });
            }}
          >
            {isAllocating ? 'Submitting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
